/**
 * The snippet an operator copies off the Install screen. An app install has
 * none: the passenger app asks at launch which install it reports to
 * (services/siteIntegrations.js#appConfig), so its Install section is a switch.
 *
 * Pure and separate from the component for one reason: **the collector host is
 * not this origin, and nothing else in the app would notice if it were wrong.**
 *
 * `analytics.js` is served from the CONSOLE origin (Firebase Hosting) but must
 * post to the SERVICE (`crmBase`), and those are different hosts. The tag
 * defaults its collector to its own script origin, so a snippet that omits
 * `apiBase` sends every beacon to Firebase — where `firebase.json` rewrites
 * `**` to `/index.html` and answers 200 with HTML. The tag only acts on a 202,
 * so it discards that silently: the install looks correct, logs nothing, and
 * reports zero forever.
 *
 * That is invisible to a component test, to typecheck, and to anyone reading
 * the screen. It is visible to `snippet.test.ts`, which is why this function
 * exists at all.
 */

export interface WebSnippetInput {
  siteToken: string;
  /** The SERVICE base, e.g. https://backend.limosphere.co/crm — `config.crmBase`. */
  crmBase: string;
  /** Where analytics.js is served from — this console's own origin. */
  consoleOrigin: string;
}

const trimSlash = (value: string) => value.replace(/\/+$/, '');

/**
 * What goes in the tenant's `<head>`.
 *
 * The config object is broken across lines rather than written inline: with the
 * collector spelled out it runs past the width of the block that renders it,
 * and `.embed-snippet` scrolls rather than wraps — so a one-line form copies
 * correctly but *looks* truncated to whoever is pasting it.
 */
export const webSnippet = ({ siteToken, crmBase, consoleOrigin }: WebSnippetInput): string =>
  [
    '<script>',
    '  window.$sphereAnalytics = {',
    `    siteToken: '${siteToken}',`,
    `    apiBase: '${trimSlash(crmBase)}',`,
    '  };',
    '</script>',
    `<script async src="${trimSlash(consoleOrigin)}/analytics.js"></script>`,
  ].join('\n');
